import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../App';
import GatherFormPage from '../components/common/GatherFormPage';
import CountrySelector from '../components/travel/CountrySelector';
import TravelImagePicker from '../components/travel/TravelImagePicker';
import TravelPostCard from '../components/travel/TravelPostCard';
import { useModal } from '../context/ModalContext';
import { supabase } from '../utils/supabaseClient';
import { deleteTravelImages, uploadTravelImages } from '../utils/travelImages';
import { TRAVEL_BUDGET_OPTIONS, TRAVEL_GENDER_OPTIONS } from '../utils/travelPostLabels';

const STATUS_OPTIONS = [
  { value: 'open', label: '開放申請中' },
  { value: 'closed', label: '已關閉' },
  { value: 'completed', label: '旅行已完成' }
];

const EMPTY_FORM = {
  destination: '',
  countries: [],
  trip_start_date: '',
  trip_end_date: '',
  budget_range: '',
  max_companions: 2,
  gender_pref: 'any',
  description: '',
  status: 'open'
};

const inputStyle = {
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '48px',
  padding: '12px 14px',
  color: '#0f172a',
  background: '#f8fafc',
  border: '1px solid #dbe4ee',
  borderRadius: '14px',
  fontFamily: 'inherit',
  fontSize: '16px'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  color: '#334155',
  fontSize: '13px',
  fontWeight: '750'
};

function ChipGroup({ options, selected, onChange, label }) {
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {options.map(option => {
        const isSelected = selected === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            style={{ minHeight: '44px', padding: '9px 15px', color: isSelected ? '#ffffff' : '#475569', background: isSelected ? '#4e8bca' : '#ffffff', border: isSelected ? '1px solid #4e8bca' : '1px solid #dbe4ee', borderRadius: '999px', boxShadow: isSelected ? '0 5px 14px rgba(78,139,202,0.22)' : 'none', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default function EditTravelPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const { showAlert } = useModal();
  const [form, setForm] = useState(EMPTY_FORM);
  const [images, setImages] = useState([]);
  const [originalImageUrls, setOriginalImageUrls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const [showCountryModal, setShowCountryModal] = useState(false);

  const loadPost = useCallback(async () => {
    if (!currentUser?.id) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase.from('travel_posts').select('*').eq('id', id).single();
      if (error) throw error;
      if (data.organizer_id !== currentUser.id) {
        showAlert('提示', '你沒有權限編輯這則貼文');
        navigate(`/travel/${id}`, { replace: true });
        return;
      }
      if (data.status === 'cancelled') {
        showAlert('提示', '已取消的揪旅伴只能查看歷史紀錄，無法再編輯');
        navigate(`/travel/${id}`, { replace: true });
        return;
      }
      setForm({
        destination: data.destination || '',
        countries: data.countries || (data.country ? [data.country] : []),
        trip_start_date: data.trip_start_date || '',
        trip_end_date: data.trip_end_date || '',
        budget_range: data.budget_range || '',
        max_companions: data.max_companions || 2,
        gender_pref: data.gender_pref || 'any',
        description: data.description || '',
        status: data.status || 'open'
      });
      setImages((data.image_urls || []).map(url => ({ url })));
      setOriginalImageUrls(data.image_urls || []);
      setIsDirty(false);
    } catch (error) {
      console.error('loadPost error:', error);
      showAlert('錯誤', '載入貼文失敗，請稍後再試');
      navigate('/travel', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.id, id, navigate, showAlert]);

  useEffect(() => { loadPost(); }, [loadPost]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const warnBeforeUnload = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isDirty]);

  const previewPost = useMemo(() => ({
    id,
    destination: form.destination.trim() || '你的旅程標題',
    countries: form.countries,
    trip_start_date: form.trip_start_date,
    trip_end_date: form.trip_end_date,
    budget_range: form.budget_range,
    max_companions: form.max_companions,
    gender_pref: form.gender_pref,
    description: form.description.trim() || '補充旅行計畫與期待的旅伴特質，讓大家更了解這趟旅程。',
    image_urls: images.map(item => item.url),
    profiles: currentUser?.profile || {},
    travel_applications: [],
    status: form.status
  }), [currentUser?.profile, form, id, images]);

  const updateField = (field, value) => {
    setForm(previous => ({ ...previous, [field]: value }));
    setErrors(previous => ({ ...previous, [field]: undefined }));
    setIsDirty(true);
  };

  const updateImages = nextImages => {
    setImages(nextImages);
    setIsDirty(true);
  };

  const showErrors = nextErrors => {
    setErrors(nextErrors);
    const firstField = Object.keys(nextErrors)[0];
    window.requestAnimationFrame(() => {
      const field = document.getElementById(`travel-edit-${firstField}`);
      field?.focus();
      field?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.destination.trim()) nextErrors.destination = '請填寫目的地標題';
    if (form.countries.length === 0) nextErrors.countries = '請至少選擇一個國家';
    if (form.trip_start_date && form.trip_end_date && form.trip_end_date < form.trip_start_date) nextErrors.trip_end_date = '回程日不能早於出發日';
    if (Object.keys(nextErrors).length > 0) {
      showErrors(nextErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleBack = () => {
    if (isDirty && !window.confirm('尚有未儲存的變更，確定要離開嗎？')) return;
    navigate(`/travel/${id}`);
  };

  const handleSave = async () => {
    if (!validateForm() || !currentUser?.id) return;
    setIsSaving(true);
    let uploadedUrls = [];
    try {
      const newFiles = images.filter(item => item.file).map(item => item.file);
      uploadedUrls = await uploadTravelImages(currentUser.id, newFiles);
      let uploadedIndex = 0;
      const finalImageUrls = images.map(item => item.file ? uploadedUrls[uploadedIndex++] : item.url);
      const { error } = await supabase
        .from('travel_posts')
        .update({
          image_urls: finalImageUrls,
          destination: form.destination.trim(),
          country: form.countries[0] || null,
          countries: form.countries,
          trip_start_date: form.trip_start_date || null,
          trip_end_date: form.trip_end_date || null,
          budget_range: form.budget_range || null,
          max_companions: form.max_companions,
          gender_pref: form.gender_pref,
          description: form.description.trim() || null,
          status: form.status
        })
        .eq('id', id);
      if (error) throw error;

      const removedUrls = originalImageUrls.filter(url => !finalImageUrls.includes(url));
      await deleteTravelImages(removedUrls);
      setIsDirty(false);
      showAlert('成功', '揪旅伴貼文已更新！');
      navigate(`/travel/${id}`, { replace: true });
    } catch (error) {
      if (uploadedUrls.length > 0) await deleteTravelImages(uploadedUrls);
      console.error('handleSave error:', error);
      showAlert('錯誤', '更新失敗，請稍後再試');
    } finally {
      setIsSaving(false);
    }
  };

  const ErrorText = ({ field }) => errors[field] ? <p role="alert" style={{ margin: '6px 0 0', color: '#dc2626', fontSize: '12px', fontWeight: '650' }}>{errors[field]}</p> : null;
  const errorBorder = field => errors[field] ? '#ef4444' : '#dbe4ee';

  return (
    <GatherFormPage
      title="編輯揪旅伴"
      subtitle="更新旅行計畫與招募條件"
      backLabel="返回揪旅伴詳情"
      onBack={handleBack}
      preview={<TravelPostCard post={previewPost} isPreview />}
      previewTitle="變更預覽"
      primaryLabel="儲存變更"
      savingLabel="儲存中..."
      onPrimary={handleSave}
      isSaving={isSaving}
      isLoading={isLoading}
    >
      <section className="gather-form-section" aria-labelledby="travel-edit-basic-heading">
        <div className="gather-form-section-header"><h2 id="travel-edit-basic-heading">基本資訊</h2><p>調整目的地與旅行地區，右側預覽會同步更新。</p></div>
        <div className="gather-form-fields">
          <div><label htmlFor="travel-edit-destination" style={labelStyle}>目的地標題 *</label><input id="travel-edit-destination" value={form.destination} onChange={event => updateField('destination', event.target.value)} placeholder="例：沖繩自由行、京阪神跨年" style={{ ...inputStyle, borderColor: errorBorder('destination') }} /><ErrorText field="destination" /></div>
          <div><label htmlFor="travel-edit-countries" style={labelStyle}>關聯國家（可多選）*</label><button id="travel-edit-countries" type="button" onClick={() => setShowCountryModal(true)} style={{ ...inputStyle, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '7px', minHeight: '50px', cursor: 'pointer', textAlign: 'left', borderColor: errorBorder('countries') }}>{form.countries.length === 0 && <span style={{ color: '#94a3b8' }}>點擊選擇國家...</span>}{form.countries.map(country => <span key={country} style={{ padding: '4px 9px', color: '#376ba1', background: '#eef4f9', borderRadius: '999px', fontSize: '12px', fontWeight: '700' }}>{country}</span>)}</button><ErrorText field="countries" /></div>
        </div>
      </section>

      <section className="gather-form-section" aria-labelledby="travel-edit-date-heading">
        <div className="gather-form-section-header"><h2 id="travel-edit-date-heading">旅行日期</h2><p>更新出發與回程日期。</p></div>
        <div className="gather-form-fields-2">
          <div><label htmlFor="travel-edit-trip_start_date" style={labelStyle}>出發日</label><input id="travel-edit-trip_start_date" type="date" value={form.trip_start_date} max={form.trip_end_date || undefined} onChange={event => updateField('trip_start_date', event.target.value)} style={inputStyle} /></div>
          <div><label htmlFor="travel-edit-trip_end_date" style={labelStyle}>回程日</label><input id="travel-edit-trip_end_date" type="date" value={form.trip_end_date} min={form.trip_start_date || undefined} onChange={event => updateField('trip_end_date', event.target.value)} style={{ ...inputStyle, borderColor: errorBorder('trip_end_date') }} /><ErrorText field="trip_end_date" /></div>
        </div>
      </section>

      <section className="gather-form-section" aria-labelledby="travel-edit-condition-heading">
        <div className="gather-form-section-header"><h2 id="travel-edit-condition-heading">人數與旅伴條件</h2><p>管理招募狀態、人數、預算與旅伴偏好。</p></div>
        <div className="gather-form-fields">
          <div><span style={labelStyle}>貼文狀態</span><ChipGroup label="貼文狀態" options={STATUS_OPTIONS} selected={form.status} onChange={value => updateField('status', value)} /></div>
          <div><span style={labelStyle}>預算等級</span><ChipGroup label="預算等級" options={TRAVEL_BUDGET_OPTIONS} selected={form.budget_range} onChange={value => updateField('budget_range', value)} /></div>
          <div className="gather-form-fields-2">
            <div><label htmlFor="travel-edit-max_companions" style={labelStyle}>想找幾位旅伴</label><select id="travel-edit-max_companions" value={form.max_companions} onChange={event => updateField('max_companions', Number(event.target.value))} style={inputStyle}>{[1, 2, 3, 4, 5, 6, 7, 8].map(number => <option key={number} value={number}>{number}人</option>)}</select></div>
            <div><span style={labelStyle}>旅伴性別偏好</span><ChipGroup label="旅伴性別偏好" options={TRAVEL_GENDER_OPTIONS} selected={form.gender_pref} onChange={value => updateField('gender_pref', value)} /></div>
          </div>
        </div>
      </section>

      <section className="gather-form-section" aria-labelledby="travel-edit-detail-heading">
        <div className="gather-form-section-header"><h2 id="travel-edit-detail-heading">詳細說明與照片</h2><p>更新旅行風格、預計行程與旅程照片。</p></div>
        <div className="gather-form-fields">
          <div><label htmlFor="travel-edit-description" style={labelStyle}>旅行描述</label><textarea id="travel-edit-description" value={form.description} onChange={event => updateField('description', event.target.value)} placeholder="介紹這次旅行的計畫、風格、希望旅伴具備什麼條件..." rows={8} style={{ ...inputStyle, minHeight: '164px', resize: 'vertical', lineHeight: 1.65 }} /></div>
          <div><span style={labelStyle}>旅程照片（最多5張，第一張為封面）</span><div style={{ padding: '14px', background: '#f8fafc', border: '1px dashed #bfd5e9', borderRadius: '16px' }}><TravelImagePicker items={images} onChange={updateImages} /></div></div>
        </div>
      </section>

      {showCountryModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(15,23,42,0.56)' }}>
          <div role="dialog" aria-modal="true" aria-label="選擇國家" style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '430px', maxHeight: '82vh', overflow: 'hidden', background: '#ffffff', borderRadius: '20px', boxShadow: '0 24px 60px rgba(15,23,42,0.24)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 18px', borderBottom: '1px solid #e2e8f0' }}><h3 style={{ margin: 0, color: '#0f172a', fontSize: '17px', fontWeight: '800' }}>選擇國家</h3><button type="button" onClick={() => setShowCountryModal(false)} style={{ minHeight: '44px', padding: '7px 14px', color: '#ffffff', background: '#4e8bca', border: 'none', borderRadius: '10px', fontFamily: 'inherit', fontSize: '13px', fontWeight: '800', cursor: 'pointer' }}>完成</button></div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}><CountrySelector selectedCountries={form.countries} onChange={value => updateField('countries', value)} multiSelect /></div>
          </div>
        </div>
      )}
    </GatherFormPage>
  );
}
